import React, { useState, useEffect, useRef } from 'react';
import { View, StyleSheet, Platform, Text, ScrollView, ActivityIndicator } from 'react-native';
import { ScreenLayout, Header, FormHeader } from '../components';
import { DispatchRuleForm, DispatchRuleFormData } from '../components/dispatch-rules/DispatchRuleForm';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface DispatchRuleEditScreenProps {
  ruleId?: string;
  scope?: 'reply_item' | 'dataset' | 'channel_settings';
  attachedTo?: string;
  attachedToLabel?: string;
}

export const DispatchRuleEditScreen: React.FC<DispatchRuleEditScreenProps> = ({ ruleId, scope, attachedTo, attachedToLabel }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const isEditing = !!ruleId;
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [rule, setRule] = useState<any>(null);
  const formSubmitRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (isEditing) {
      loadRule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleId]);

  useEffect(() => {
    if (!isEditing && !scope) {
      showNotification('error', 'Error', 'Rule scope and target are required');
      navigateTo('quick-reply-items');
    }
  }, [isEditing, scope, showNotification, navigateTo]);

  const loadRule = async () => {
    if (!ruleId) return;
    try {
      setLoading(true);
      const response = await apiService.getDispatchRuleById(ruleId);
      if (response.statusCode === 1000 && response.data) {
        setRule(response.data);
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to load rule';
        showNotification('error', 'Error', errorMessage);
        navigateTo('quick-reply-items');
      }
    } catch (error: any) {
      console.error('Error loading dispatch rule:', error);
      showNotification('error', 'Error', error.message || 'Failed to load rule');
      navigateTo('quick-reply-items');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData: DispatchRuleFormData) => {
    try {
      setSaving(true);
      if (isEditing && ruleId) {
        const response = await apiService.updateDispatchRule(ruleId, {
          name: formData.name,
          channelIdentifier: formData.channelIdentifier,
          conditionGroups: formData.conditionGroups,
          defaultOutcome: formData.defaultOutcome,
          isActive: formData.isActive,
        });
        if (response.statusCode === 1000) {
          showNotification('success', 'Success', 'Rule updated successfully.');
          handleBack();
        } else {
          showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to update rule');
        }
      } else {
        if (!scope || !attachedTo) return;
        const response = await apiService.createDispatchRule({
          name: formData.name,
          scope,
          attachedTo,
          channel: 'whatsapp',
          channelIdentifier: formData.channelIdentifier,
          conditionGroups: formData.conditionGroups,
          defaultOutcome: formData.defaultOutcome,
          isActive: formData.isActive,
        });
        if (response.statusCode === 1000) {
          showNotification('success', 'Success', 'Rule created successfully.');
          handleBack();
        } else {
          showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to create rule');
        }
      }
    } catch (error: any) {
      console.error('Error saving dispatch rule:', error);
      showNotification('error', 'Error', error.message || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('dispatch-rules-list', undefined, { scope, attachedTo, attachedToLabel });
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const effectiveScope = isEditing ? rule?.scope : scope;
  const effectiveAttachedTo = isEditing ? rule?.attachedTo : attachedTo;

  if (loading) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
        rightPaneContent="empty"
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading rule...</Text>
        </View>
      </ScreenLayout>
    );
  }

  if (isEditing && !rule) {
    return null;
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      rightPaneContent="empty"
      formHeader={
        <FormHeader
          title={isEditing ? 'Edit Rule' : 'New Rule'}
          subtitle={attachedToLabel ? `Attached to "${attachedToLabel}"` : 'Configure conditional dispatch logic'}
          onBack={handleBack}
          actions={[
            {
              icon: 'checkmark',
              onPress: () => formSubmitRef.current?.(),
              primary: true,
              loading: saving,
              disabled: saving,
            },
          ]}
        />
      }
    >
      <View style={styles.wrapper}>
        <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer} showsVerticalScrollIndicator={false}>
          <View style={styles.formContainer}>
            <DispatchRuleForm
              initialData={{
                _id: rule?._id,
                name: rule?.name,
                conditionGroups: rule?.conditionGroups,
                defaultOutcome: rule?.defaultOutcome,
                isActive: rule?.isActive,
              }}
              scope={effectiveScope}
              attachedTo={effectiveAttachedTo}
              onSubmit={handleSubmit}
              onCancel={handleBack}
              submitLabel={isEditing ? 'Save Changes' : 'Create Rule'}
              loading={saving}
              hideActions={true}
              submitRef={formSubmitRef}
            />
          </View>
        </ScrollView>
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
  },
  contentContainer: {
    paddingBottom: spacing.xl,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  formContainer: {
    padding: spacing.md,
  },
});
